import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { api_urls } from "../../../utils/api-urls";

const PACKAGE_TYPES = [
  { value: "Bronze", label: "Bronze" },
  { value: "Silver", label: "Silver" },
  { value: "Gold", label: "Gold" },
  { value: "Platinum", label: "Platinum" }
];

const MilanSathiPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    packageType: "Standard",
    description: "",
    price: "",
    offerPrice: "",
    duration: "",
    connectionLimit: "",
    features: "",
    isActive: true,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch(api_urls + "api/admins/plans");
      const data = await response.json();
      if (data.success) {
        setPlans(data.data.sort((a, b) => b.price - a.price));
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setFormData({
        _id: plan._id,
        name: plan.name || "",
        packageType: plan.packageType || "Standard",
        description: plan.description || "",
        price: plan.price || "",
        offerPrice: plan.offerPrice || "",
        duration: plan.duration || "",
        connectionLimit: plan.connectionLimit || "",
        features: Array.isArray(plan.features) ? plan.features.join(", ") : plan.features || "",
        isActive: plan.isActive !== false,
      });
      setEditMode(true);
    } else {
      setFormData({
        name: "",
        packageType: "Standard",
        description: "",
        price: "",
        offerPrice: "",
        duration: "",
        connectionLimit: "",
        features: "",
        isActive: true,
      });
      setEditMode(false);
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        name: formData.name,
        packageType: formData.packageType || "Standard",
        description: formData.description || "",
        price: Number(formData.price),
        offerPrice: formData.offerPrice ? Number(formData.offerPrice) : null,
        duration: String(formData.duration || "30"),
        connectionLimit: String(formData.connectionLimit || "Unlimited"),
        isActive: formData.isActive !== false,
        features: formData.features
          ? (Array.isArray(formData.features) ? formData.features : formData.features.split(",").map((f) => f.trim()).filter((f) => f))
          : [],
      };

      let response;
      if (editMode) {
        response = await fetch(api_urls + "api/admins/plans/" + formData._id, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(api_urls + "api/admins/plans", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      const data = await response.json();
      if (data.success) {
        fetchPlans();
        setDialogOpen(false);
      } else {
        alert(data.message || "Failed to save plan");
      }
    } catch (error) {
      console.error("Error saving plan:", error);
      alert("Error saving plan");
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) return;
    try {
      const response = await fetch(api_urls + "api/admins/plans/" + planId, {
        method: "DELETE",
      });
      const data = await response.json();
      if (data.success) {
        fetchPlans();
      } else {
        alert(data.message || "Failed to delete plan");
      }
    } catch (error) {
      console.error("Error deleting plan:", error);
      alert("Error deleting plan");
    }
  };

  const getDiscountPercentage = (price, offerPrice) => {
    if (!offerPrice || offerPrice >= price) return 0;
    return Math.round(((price - offerPrice) / price) * 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Subscription Plans
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Plan
        </Button>
      </Box>

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>Name</TableCell>
                <TableCell>Package Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Original Price</TableCell>
                <TableCell>Offer Price</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Connection Limit</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const discount = getDiscountPercentage(plan.price, plan.offerPrice);
                return (
                  <TableRow key={plan._id} hover>
                    <TableCell>
                      <Typography fontWeight="bold">{plan.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plan.packageType || "Standard"}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: "bold" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {plan.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="textSecondary" sx={{ textDecoration: discount > 0 ? "line-through" : "none" }}>
                        ₹{plan.price?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {plan.offerPrice ? (
                        <Typography fontWeight="bold" color="primary">
                          ₹{plan.offerPrice?.toLocaleString()}
                        </Typography>
                      ) : (
                        <Typography color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {discount > 0 ? (
                        <Chip
                          label={discount + "% OFF"}
                          color="error"
                          size="small"
                          sx={{ fontWeight: "bold" }}
                        />
                      ) : (
                        <Typography color="textSecondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{plan.duration} days</TableCell>
                    <TableCell>{plan.connectionLimit || "Unlimited"}</TableCell>
                    <TableCell>
                      <Chip
                        label={plan.isActive !== false ? "Active" : "Inactive"}
                        color={plan.isActive !== false ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(plan)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(plan._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {plans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="textSecondary">No plans found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add/Edit Plan Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? "Edit Plan" : "Add New Plan"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Package Type"
                value={formData.packageType || "Standard"}
                onChange={(e) => setFormData({ ...formData, packageType: e.target.value })}
                required
              >
                {PACKAGE_TYPES.map((pkg) => (
                  <MenuItem key={pkg.value} value={pkg.value}>
                    {pkg.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Original Price (₹)"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
                helperText="MRP / Full Price"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Offer Price (₹)"
                type="number"
                value={formData.offerPrice}
                onChange={(e) => setFormData({ ...formData, offerPrice: e.target.value })}
                helperText="Sale price (leave empty for no discount)"
              />
            </Grid>
            {formData.price && formData.offerPrice && Number(formData.offerPrice) < Number(formData.price) && (
              <Grid item xs={12}>
                <Chip
                  label={"Discount Preview: " + getDiscountPercentage(Number(formData.price), Number(formData.offerPrice)) + "% OFF"}
                  color="error"
                  sx={{ fontWeight: "bold" }}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Duration (days)"
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Connection Limit"
                type="number"
                value={formData.connectionLimit}
                onChange={(e) => setFormData({ ...formData, connectionLimit: e.target.value })}
                helperText="Leave empty for unlimited"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features (comma separated)"
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                multiline
                rows={2}
                placeholder="e.g., Unlimited Messages, View Contact, Priority Support"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editMode ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MilanSathiPlans;
